import isEqual from 'lodash/isEqual';

import {appendFieldError, createInputField, createTextareaField, disableFormControls, normalizeFieldErrors} from '../forms/formFields';
import {cloneValue} from '../forms/formState';
import {createRemoveIcon} from '../forms/repeatableFields';
import {createChatModelForm, normalizeChatModel} from './chatModelForm';
import {createMCPServersForm, normalizeMCPServers} from './mcpServerForm';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwnProperty = (value, property) => Object.prototype.hasOwnProperty.call(value, property);

const maxPersistedMessageCount = 5;
const messageFieldNames = ['userMessages', 'systemMessages'];
const messageEditorMinHeight = '150px';

const defaultRequiredChatModel = {
    credentials: '',
    provider: '',
    model: ''
};

const normalizeStringList = value => Array.isArray(value)
    ? value.map(item => typeof item === 'undefined' || item === null ? '' : String(item)).filter(item => item.length > 0)
    : value;

const normalizeMessageList = value => {
    if (!Array.isArray(value)) {
        return value;
    }

    const messages = normalizeStringList(value);
    return value.length > maxPersistedMessageCount && messages.length > 0
        ? [messages.join('\n\n')]
        : messages;
};

const hasMessageOverflow = value => isPlainObject(value) && messageFieldNames.some(fieldName =>
    Array.isArray(value[fieldName]) && value[fieldName].length > maxPersistedMessageCount);

const normalizeAIChat = aiChat => {
    if (!isPlainObject(aiChat)) {
        return {
            chatModel: normalizeChatModel(defaultRequiredChatModel)
        };
    }

    const normalizedAIChat = {
        chatModel: normalizeChatModel({
            ...defaultRequiredChatModel,
            ...(isPlainObject(aiChat.chatModel) ? aiChat.chatModel : {})
        })
    };
    if (hasOwnProperty(aiChat, 'userMessages')) {
        normalizedAIChat.userMessages = normalizeMessageList(aiChat.userMessages);
    }
    if (hasOwnProperty(aiChat, 'systemMessages')) {
        normalizedAIChat.systemMessages = normalizeMessageList(aiChat.systemMessages);
    }
    if (hasOwnProperty(aiChat, 'mcpServers')) {
        normalizedAIChat.mcpServers = normalizeMCPServers(aiChat.mcpServers);
    }
    if (hasOwnProperty(aiChat, 'memoryWindowLength')) {
        const value = aiChat.memoryWindowLength;
        normalizedAIChat.memoryWindowLength = value === '' ? null : typeof value === 'string' ? Number(value) : value;
    }

    return normalizedAIChat;
};

const normalizeAIChatPatch = (aiChat, patch) => {
    const normalizedPatch = cloneValue(patch || {});

    Object.keys(normalizedPatch).forEach(fieldName => {
        const value = normalizedPatch[fieldName];
        const emptyArray = Array.isArray(value) && value.length === 0;
        if (value !== null && typeof value !== 'undefined' && !emptyArray) {
            return;
        }

        if (!hasOwnProperty(aiChat, fieldName)) {
            delete normalizedPatch[fieldName];
        }
    });

    return normalizedPatch;
};

const getResource = (mxResources, key) => mxResources.get(key);

const listFieldValue = (aiChat, fieldName) => Array.isArray(aiChat[fieldName]) ? aiChat[fieldName] : [];

const arrayError = error => Array.isArray(error) ? error.filter(Boolean) : error;

const markdownTextareaValue = value => {
    const stringValue = value || '';
    return stringValue.length > 0 && !stringValue.endsWith('\n') ? `${stringValue}\n` : stringValue;
};

const storedMarkdownValue = value => {
    const stringValue = value || '';
    return stringValue.endsWith('\n') ? stringValue.slice(0, -1) : stringValue;
};

const createErrorCountLabel = error => {
    const errorCount = normalizeFieldErrors(error).length;
    if (errorCount === 0) {
        return null;
    }

    const errorLabel = document.createElement('span');
    errorLabel.className = 'ui mini red circular label';
    errorLabel.textContent = String(errorCount);
    return errorLabel;
};

const createMessageTabItem = ({id, label, error, active = false}) => {
    const tab = document.createElement('a');
    tab.className = `${active ? 'active ' : ''}item`;
    tab.setAttribute('data-tab', id);
    tab.appendChild(document.createTextNode(label));

    const errorLabel = createErrorCountLabel(error);
    if (errorLabel) {
        tab.appendChild(errorLabel);
    }

    return tab;
};

const createMessageTabSegment = ({id, field, active = false}) => {
    const segment = document.createElement('div');
    segment.className = `ui bottom attached ${active ? 'active ' : ''}tab segment ai-chat-message-tab-segment`;
    segment.setAttribute('data-tab', id);
    segment.appendChild(field);
    return segment;
};

const messageTabFields = {
    aiChatUserMessageTab: 'userMessages',
    aiChatSystemMessageTab: 'systemMessages'
};

const messageFieldPresence = value => messageFieldNames.reduce((presence, fieldName) => ({
    ...presence,
    [fieldName]: isPlainObject(value) && hasOwnProperty(value, fieldName)
}), {});

const mountMessageEditor = (textarea, onChange) => {
    let editorControl = null;
    let destroyed = false;
    let currentValue = textarea.value;
    let pendingRefresh = false;

    const emitChange = value => {
        if (textarea.disabled || textarea.readOnly) {
            return;
        }
        if (currentValue === value) {
            return;
        }
        currentValue = value;
        onChange(value);
    };

    textarea.addEventListener('change', event => {
        if (!textarea.disabled && !textarea.readOnly) {
            emitChange(event.target.value);
        }
    });

    import(/* webpackChunkName: "text-editor" */ '../../TextEditor')
        .then(module => {
            if (destroyed) {
                return;
            }
            editorControl = module.textEditor(textarea, textarea.value, {onChange: emitChange});
            if (pendingRefresh) {
                pendingRefresh = false;
                editorControl.refresh?.();
            }
        })
        .catch(() => {
        });

    return {
        sync() {
            if (editorControl?.sync) {
                return editorControl.sync();
            }
            if (!textarea.disabled && !textarea.readOnly) {
                emitChange(textarea.value);
            }
            return textarea.value;
        },
        refresh() {
            if (editorControl?.refresh) {
                editorControl.refresh();
                return;
            }
            pendingRefresh = true;
        },
        destroy() {
            destroyed = true;
            pendingRefresh = false;
            editorControl?.destroy?.();
        }
    };
};

const createAIChatTaskForm = ({
                                  task = {},
                                  taskErrors = {},
                                  editable = true,
                                  mxResources,
                                  messagesMountElement = null,
                                  chatModelMountElement = null,
                                  mcpServersMountElement = null,
                                  activeMessageTabId: initialActiveMessageTabId = 'aiChatUserMessageTab',
                                  createTestAIChatField = null,
                                  onAIChatChange = () => {},
                                  onRender = () => {},
                                  onProviderChange = () => {}
                              } = {}) => {
    let aiChat = normalizeAIChat(task);
    let codeMirrorModule = null;
    let chatModelForm = null;
    let chatModelListingCache = {};
    let mcpServersForm = null;
    let mcpServersToolDiscoveryCache = {};
    let messageEditorControls = [];
    let activeMessageTabId = messageTabFields[initialActiveMessageTabId]
        ? initialActiveMessageTabId
        : 'aiChatUserMessageTab';
    let initialMessageFieldPresence = messageFieldPresence(task);
    let syncingMessageEditors = false;
    let destroyed = false;
    let overflowNormalizationTimeoutId = null;
    let jsonExpressionHelp = null;
    let memoryWindowValid = true;

    const container = document.createElement('div');
    container.id = 'aiChatEditor';
    container.className = 'ui form';

    const messagesElement = messagesMountElement || document.createElement('div');
    messagesElement.id = 'aiChatMessagesEditor';
    const chatModelElement = chatModelMountElement || document.createElement('div');
    chatModelElement.id = 'aiChatModelEditor';
    const mcpServersElement = mcpServersMountElement || document.createElement('div');
    mcpServersElement.id = 'aiChatMCPServersEditor';
    if (!messagesMountElement) {
        container.appendChild(messagesElement);
    }
    if (!chatModelMountElement) {
        container.appendChild(chatModelElement);
    }
    if (!mcpServersMountElement) {
        container.appendChild(mcpServersElement);
    }

    const readMessageFieldValues = fieldName => normalizeStringList(
        [...messagesElement.querySelectorAll('textarea')]
            .filter(textarea => textarea.name === fieldName)
            .map(textarea => storedMarkdownValue(textarea.value))
    );

    const renderMessageChange = fieldName => {
        if (syncingMessageEditors) {
            return;
        }

        if (emitAIChat({[fieldName]: readMessageFieldValues(fieldName)})) {
            renderMessagesFields();
            onRender(container);
            refreshMessageEditor(fieldName);
        }
    };

    const syncMessageEditorTextareas = () => {
        syncingMessageEditors = true;
        try {
            messageEditorControls.forEach(({editorControl}) => editorControl.sync?.());
        } finally {
            syncingMessageEditors = false;
        }
    };

    const removeMessageRow = (row, textarea) => {
        syncMessageEditorTextareas();

        const fieldName = textarea.name;
        row.remove();
        emitAIChat({[fieldName]: readMessageFieldValues(fieldName)});
        renderMessagesFields();
        onRender(container);
        refreshMessageEditor(fieldName);
    };

    const appendJsonExpressionHelpToField = field => {
        if (!jsonExpressionHelp || field.querySelector('details')) {
            return;
        }

        field.insertAdjacentHTML('beforeend', jsonExpressionHelp);
    };

    const createMessageField = ({fieldName, id}) => {
        const field = document.createElement('div');
        field.className = 'field ai-chat-message-list-field';

        const list = document.createElement('div');
        list.className = 'ui form repeatable string-list ai-chat-message-list';

        const values = listFieldValue(aiChat, fieldName);
        const rowValues = editable && values.length < maxPersistedMessageCount ? [...values, ''] : values;
        rowValues.forEach((item, index) => {
            const blankInputRow = editable && index === values.length;
            const row = document.createElement('div');
            row.className = editable && !blankInputRow
                ? 'field repeatable-string-list-row repeatable-focus-remove-row ai-chat-message-list-row'
                : 'field repeatable-string-list-row ai-chat-message-list-row';

            const textareaField = createTextareaField({
                id: `${id}-${index}`,
                name: fieldName,
                rows: 3,
                value: markdownTextareaValue(item)
            });
            const textarea = textareaField.querySelector('textarea');
            textarea.value = markdownTextareaValue(item);
            textarea.classList.add('markdown-editor');
            textarea.dataset.textEditorMinHeight = messageEditorMinHeight;
            if (!editable) {
                textarea.disabled = true;
                textarea.readOnly = true;
                textarea.setAttribute('disabled', '');
                textarea.setAttribute('readonly', '');
            }

            messageEditorControls.push({
                fieldName,
                editorControl: mountMessageEditor(
                    textarea,
                    () => renderMessageChange(fieldName)
                )
            });

            row.appendChild(textareaField);

            if (editable && !blankInputRow) {
                const removeControl = createRemoveIcon(getResource(mxResources, 'aiChatRemoveMessage'));
                let rowRemoved = false;
                const removeCurrentRow = event => {
                    event?.preventDefault?.();
                    event?.stopPropagation?.();
                    if (rowRemoved) {
                        return;
                    }
                    rowRemoved = true;
                    removeMessageRow(row, textarea);
                };
                removeControl.addEventListener('pointerdown', removeCurrentRow);
                removeControl.addEventListener('mousedown', removeCurrentRow);
                removeControl.addEventListener('click', removeCurrentRow);
                removeControl.addEventListener('keydown', event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        removeCurrentRow(event);
                    }
                });
                row.appendChild(removeControl);
            }

            list.appendChild(row);
        });

        field.appendChild(list);
        appendFieldError(field, arrayError(taskErrors?.fields?.[fieldName]));
        appendJsonExpressionHelpToField(field);
        return field;
    };

    const destroyMessageEditors = () => {
        messageEditorControls.forEach(({editorControl}) => editorControl.destroy());
        messageEditorControls = [];
    };

    const syncMessageEditors = () => {
        syncMessageEditorTextareas();
        messageFieldNames.forEach(fieldName => {
            emitAIChat({[fieldName]: readMessageFieldValues(fieldName)});
        });
    };

    const syncAndDestroyMessageEditors = () => {
        syncMessageEditors();
        destroyMessageEditors();
    };

    const clearOverflowNormalization = () => {
        if (overflowNormalizationTimeoutId !== null) {
            clearTimeout(overflowNormalizationTimeoutId);
            overflowNormalizationTimeoutId = null;
        }
    };

    const refreshMessageEditor = fieldName => {
        messageEditorControls
            .filter(messageEditorControl => messageEditorControl.fieldName === fieldName)
            .forEach(messageEditorControl => messageEditorControl.editorControl.refresh?.());
    };

    const activeMessageFieldName = () => messageTabFields[activeMessageTabId] || 'userMessages';

    const refreshActiveMessageEditor = () => refreshMessageEditor(activeMessageFieldName());

    const renderMessagesFields = () => {
        destroyMessageEditors();

        const userMessagesField = createMessageField({
            fieldName: 'userMessages',
            id: 'aiChatUserMessage'
        });
        const systemMessagesField = createMessageField({
            fieldName: 'systemMessages',
            id: 'aiChatSystemMessage'
        });
        const messageTabsField = document.createElement('div');
        messageTabsField.className = 'field ai-chat-message-tabs-field';

        const messageTabMenu = document.createElement('div');
        messageTabMenu.className = 'ui top attached tabular menu ai-chat-message-tab-menu';
        messageTabMenu.appendChild(createMessageTabItem({
            id: 'aiChatUserMessageTab',
            label: getResource(mxResources, 'aiChatUserMessage'),
            error: taskErrors?.fields?.userMessages,
            active: activeMessageTabId === 'aiChatUserMessageTab'
        }));
        messageTabMenu.appendChild(createMessageTabItem({
            id: 'aiChatSystemMessageTab',
            label: getResource(mxResources, 'aiChatSystemMessage'),
            error: taskErrors?.fields?.systemMessages,
            active: activeMessageTabId === 'aiChatSystemMessageTab'
        }));

        messageTabsField.appendChild(messageTabMenu);
        messageTabsField.appendChild(createMessageTabSegment({
            id: 'aiChatUserMessageTab',
            field: userMessagesField,
            active: activeMessageTabId === 'aiChatUserMessageTab'
        }));
        messageTabsField.appendChild(createMessageTabSegment({
            id: 'aiChatSystemMessageTab',
            field: systemMessagesField,
            active: activeMessageTabId === 'aiChatSystemMessageTab'
        }));

        const messageActions = document.createElement('div');
        messageActions.className = 'fields ai-chat-message-actions';
        const testAIChatField = createTestAIChatField?.('messages');
        if (testAIChatField) {
            messageActions.appendChild(testAIChatField);
        }

        const memoryEnabled = aiChat.memoryWindowLength != null;
        memoryWindowValid = !memoryEnabled || (Number.isInteger(aiChat.memoryWindowLength) &&
            aiChat.memoryWindowLength >= 2 && aiChat.memoryWindowLength <= 100);
        const memoryWindowField = createInputField({
            label: getResource(mxResources, 'aiChatMemoryWindowLength'),
            id: 'aiChatMemoryWindowLength',
            name: 'memoryWindowLength',
            type: 'number',
            min: 2,
            step: 1,
            value: memoryEnabled ? aiChat.memoryWindowLength : '',
            help: getResource(mxResources, 'aiChatMemoryWindowHelp'),
            error: memoryWindowValid ? taskErrors?.fields?.memoryWindowLength : getResource(mxResources, 'aiChatMemoryWindowInvalid')
        });
        const memoryWindowInput = memoryWindowField.querySelector('input');
        memoryWindowInput.max = 100;
        memoryWindowInput.addEventListener('change', event => {
            syncMessageEditors();
            emitAIChat({memoryWindowLength: event.target.value === '' ? null : Number(event.target.value)});
            renderMessagesFields();
            onRender(container);
        });
        messagesElement.replaceChildren(
            memoryWindowField,
            messageTabsField,
            ...(messageActions.children.length > 0 ? [messageActions] : [])
        );
        if (!editable) {
            disableFormControls(memoryWindowField);
        }
    };

    const renderChatModelForm = () => {
        chatModelListingCache = chatModelForm?.getModelListingCache?.() || chatModelListingCache;
        chatModelForm = createChatModelForm({
            chatModel: aiChat.chatModel,
            chatModelErrors: taskErrors?.fields?.chatModel,
            editable,
            mxResources,
            mountElement: chatModelElement,
            createActionField: () => createTestAIChatField?.('model'),
            modelListingCache: chatModelListingCache,
            onRender: () => onRender(container),
            onProviderChange,
            onChange: chatModel => emitAIChat({chatModel})
        });
        if (codeMirrorModule) {
            chatModelForm.mountResponseJSONSchemaEditor(codeMirrorModule);
        }
    };

    const renderMCPServersForm = () => {
        mcpServersToolDiscoveryCache = mcpServersForm?.getToolDiscoveryCache?.() || mcpServersToolDiscoveryCache;
        mcpServersForm = createMCPServersForm({
            mcpServers: aiChat.mcpServers,
            mcpServerErrors: taskErrors?.fields?.mcpServers,
            editable,
            mxResources,
            mountElement: mcpServersElement,
            createActionField: () => createTestAIChatField?.('mcpServers'),
            toolDiscoveryCache: mcpServersToolDiscoveryCache,
            onRender: () => onRender(container),
            onChange: mcpServers => emitAIChat({mcpServers})
        });
    };

    const syncStructuredFields = () => {
        renderMessagesFields();
        renderChatModelForm();
        renderMCPServersForm();
        onRender(container);
    };

    const scheduleOverflowNormalizedAIChat = value => {
        clearOverflowNormalization();

        if (!editable || destroyed || !hasMessageOverflow(value)) {
            return;
        }

        overflowNormalizationTimeoutId = setTimeout(() => {
            overflowNormalizationTimeoutId = null;
            if (!destroyed) {
                onAIChatChange(cloneValue(aiChat));
            }
        }, 0);
    };

    function emitAIChat(patch) {
        const normalizedPatch = normalizeAIChatPatch(aiChat, patch);
        const patchedAIChat = {
            ...aiChat,
            ...normalizedPatch
        };
        messageFieldNames.forEach(fieldName => {
            if (hasOwnProperty(patch, fieldName) &&
                Array.isArray(patch[fieldName]) &&
                patch[fieldName].length === 0 &&
                !initialMessageFieldPresence[fieldName]) {
                delete patchedAIChat[fieldName];
            }
        });

        const nextAIChat = normalizeAIChat(patchedAIChat);

        if (isEqual(aiChat, nextAIChat)) {
            return false;
        }

        aiChat = nextAIChat;
        clearOverflowNormalization();
        onAIChatChange(cloneValue(aiChat));
        return true;
    }

    syncStructuredFields();
    scheduleOverflowNormalizedAIChat(task);

    if (!editable) {
        [container, messagesElement, chatModelElement, mcpServersElement].forEach(formElement => {
            disableFormControls(formElement);
        });
    }

    const sync = () => {
        if (!destroyed) {
            mcpServersForm?.sync?.();
            syncMessageEditors();
        }
    };

    return {
        element: container,
        messagesElement,
        chatModelElement,
        mcpServersElement,
        sync,
        getAIChat: () => {
            sync();
            return cloneValue(aiChat);
        },
        setAIChat: value => {
            destroyMessageEditors();
            aiChat = normalizeAIChat(value);
            initialMessageFieldPresence = messageFieldPresence(value);
            syncStructuredFields();
            scheduleOverflowNormalizedAIChat(value);
        },
        isValid: () => {
            sync();
            renderMessagesFields();
            onRender(container);
            return chatModelForm?.isValid() !== false && memoryWindowValid;
        },
        mountResponseJSONSchemaEditor: codeEditorModule => {
            codeMirrorModule = codeEditorModule;
            chatModelForm?.mountResponseJSONSchemaEditor(codeEditorModule);
        },
        setActiveMessageTab: tabId => {
            if (messageTabFields[tabId]) {
                activeMessageTabId = tabId;
            }
        },
        refreshActiveMessageEditor,
        appendJsonExpressionHelp: help => {
            jsonExpressionHelp = help;
            messagesElement.querySelectorAll('.ai-chat-message-list-field')
                .forEach(appendJsonExpressionHelpToField);
        },
        destroy: () => {
            if (destroyed) {
                return;
            }
            destroyed = true;
            mcpServersForm?.sync?.();
            clearOverflowNormalization();
            syncAndDestroyMessageEditors();
        }
    };
};

export {
    createAIChatTaskForm,
    normalizeAIChat,
    normalizeAIChatPatch
};
